import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Chip, Paper } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import FastForwardIcon from "@mui/icons-material/FastForward";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {
  TE_STATE_CANCELLED,
  TE_STATE_FINALIZED,
  TE_STATE_PAUSED,
  TE_STATE_STARTED,
} from "../utils/coachExerciseNavigation";
import { isKotExercise, isTeSequences } from "../utils/teExecutionType";

/**
 * Mirrors CoachExerciseControl primary actions for reuse on Training floating bar (compact) or full page.
 */
export function CoachExerciseActionsBar({
  exercise,
  /** Resolved tbl_state_te.st_id / te_state string */
  stateId,
  brandPrimary = "#0877BC",
  brandPrimaryDark = "#065a8f",
  compact = false,
  onStart,
  onPause,
  onResume,
  onFinalize,
  onStartNextSequence,
  onSkipSequence,
  onSkipExercise,
  /** Optional — full exercise control screen */
  onOpenFullControl,
  /** KoT start-round button (training session controller header, right side) */
  kotStartRoundSlot = null,
  /** KoT stop-round button (STOP_SCORE_LEAD — next to Finalize) */
  kotStopRoundSlot = null,
}) {
  const { t } = useTranslation();

  if (!exercise?.te_id) return null;

  const isRunning =
    stateId === TE_STATE_STARTED || stateId === "started";
  const isPaused = stateId === TE_STATE_PAUSED || stateId === "paused";
  const isFinalized =
    stateId === TE_STATE_FINALIZED || stateId === "finalized";
  const isCancelled =
    stateId === TE_STATE_CANCELLED || stateId === "cancelled";

  const sequenceCount = exercise.sequences?.length || 0;
  const hasMultipleSequences = sequenceCount > 1;
  const seqTimerDisplay =
    exercise.sequenceTimer !== undefined && exercise.sequenceTimer !== null
      ? exercise.sequenceTimer
      : 0;
  const seqTimerNumeric = Number(seqTimerDisplay);
  const hasSequenceTimeLeft =
    Number.isFinite(seqTimerNumeric) && seqTimerNumeric > 0;

  const showStartNextSequence =
    hasMultipleSequences &&
    !!exercise.awaitingManualSequenceStart &&
    !exercise.awaitingAutoSequenceStart &&
    !hasSequenceTimeLeft;

  const isKot = isKotExercise(exercise);
  const showSkipSequence =
    !!onSkipSequence &&
    isTeSequences(exercise) &&
    (isRunning || isPaused) &&
    !isKot;
  const showSkipExercise =
    !!onSkipExercise &&
    !isRunning &&
    !isPaused &&
    !isFinalized &&
    !isCancelled;

  const btnSx = compact
    ? { py: 1, fontWeight: 700, textTransform: "none", fontSize: "0.85rem" }
    : { py: 1.5, fontWeight: 800, textTransform: "none", borderRadius: 2 };

  const flexDir = { xs: "column", sm: "row" };

  return (
    <Paper
      elevation={compact ? 8 : 0}
      sx={{
        p: compact ? 1 : 0,
        borderRadius: compact ? 2 : 0,
        bgcolor: compact ? "var(--brand-surface, #fff)" : "transparent",
        border: compact ? "1px solid rgba(var(--brand-primary-rgb), 0.12)" : "none",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
          mb: compact ? 1 : 0,
        }}
      >
        <Chip
          size="small"
          label={`${t("exercise") || "Exercise"} ${exercise.te_order ?? ""}`.trim()}
          sx={{
            fontWeight: 700,
            bgcolor: "rgba(var(--brand-primary-rgb), 0.1)",
            color: brandPrimary,
            maxWidth: compact ? "min(220px, 55vw)" : "none",
          }}
        />
        {onOpenFullControl && (
          <Box sx={{ ml: "auto", flexShrink: 0 }}>
            <Tooltip title={t("exerciseControl") || "Exercise control"}>
              <IconButton
                size="small"
                onClick={onOpenFullControl}
                aria-label={t("exerciseControl") || "Exercise control"}
                sx={{ color: brandPrimary }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: compact ? 1 : 2 }}>
        {!isRunning && !isPaused && !isFinalized && !isCancelled && (
          <Box sx={{ display: "flex", flexDirection: flexDir, gap: 1.5, width: "100%" }}>
            <Button
              variant="contained"
              size={compact ? "medium" : "large"}
              startIcon={<PlayArrowIcon />}
              onClick={() => onStart?.(exercise.te_id)}
              sx={{
                flex: 1,
                ...btnSx,
                background: `linear-gradient(135deg, ${brandPrimary} 0%, ${brandPrimaryDark} 100%)`,
                boxShadow: `0 4px 12px rgba(var(--brand-primary-rgb), 0.35)`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${brandPrimaryDark} 0%, ${brandPrimary} 100%)`,
                },
              }}
            >
              {t("startExercise") || "Start Exercise"}
            </Button>
            {showSkipExercise ? (
              <Button
                variant="outlined"
                color="warning"
                size={compact ? "medium" : "large"}
                startIcon={<CancelOutlinedIcon />}
                onClick={() => onSkipExercise?.(exercise.te_id)}
                sx={{ flex: 1, ...btnSx, borderWidth: compact ? 1 : 2 }}
              >
                {t("liveTrainingSkipExercise") || "Skip exercise"}
              </Button>
            ) : null}
          </Box>
        )}

        {showStartNextSequence && (
          <Box sx={{ width: "100%" }}>
            {!compact && (
              <Box
                component="span"
                sx={{
                  display: "block",
                  mb: 1,
                  textAlign: "center",
                  fontWeight: 600,
                  fontSize: 13,
                  color: "text.secondary",
                }}
              >
                {exercise.nextSequenceNumber != null
                  ? t("awaitingNextSequenceHintNumbered", {
                      n: exercise.nextSequenceNumber,
                    }) ||
                    `Block ${exercise.nextSequenceNumber} is ready when you are.`
                  : t("awaitingNextSequenceHint") ||
                    "Current block ended. Start the next sequence when the group is ready."}
              </Box>
            )}
            <Button
              variant="contained"
              color="success"
              size={compact ? "medium" : "large"}
              startIcon={<PlayArrowIcon />}
              onClick={() => onStartNextSequence?.(exercise.te_id)}
              fullWidth
              sx={btnSx}
            >
              {t("startNextSequence") || "Start next sequence"}
            </Button>
            {showSkipSequence ? (
              <Button
                variant="outlined"
                color="warning"
                size={compact ? "medium" : "large"}
                startIcon={<FastForwardIcon />}
                onClick={() => onSkipSequence?.(exercise.te_id)}
                fullWidth
                sx={{ mt: 1, ...btnSx, borderWidth: compact ? 1 : 2 }}
              >
                {t("liveTrainingSkipSequence") || "Skip sequence"}
              </Button>
            ) : null}
          </Box>
        )}

        {isRunning && (
          <Box sx={{ display: "flex", flexDirection: flexDir, gap: 1.5, width: "100%", alignItems: "stretch" }}>
            <Button
              variant="outlined"
              color="warning"
              size={compact ? "medium" : "large"}
              startIcon={<PauseIcon />}
              onClick={() => onPause?.(exercise.te_id)}
              sx={{ flex: 1, ...btnSx, borderWidth: compact ? 1 : 2 }}
            >
              {t("pause") || "Pause"}
            </Button>
            {kotStopRoundSlot}
            {showSkipSequence ? (
              <Button
                variant="outlined"
                color="warning"
                size={compact ? "medium" : "large"}
                startIcon={<FastForwardIcon />}
                onClick={() => onSkipSequence?.(exercise.te_id)}
                sx={{ flex: 1, ...btnSx, borderWidth: compact ? 1 : 2 }}
              >
                {t("liveTrainingSkipSequence") || "Skip sequence"}
              </Button>
            ) : null}
            <Button
              variant="contained"
              color="error"
              size={compact ? "medium" : "large"}
              startIcon={<StopIcon />}
              onClick={() => onFinalize?.()}
              sx={{ flex: 1, ...btnSx }}
            >
              {t("finalize") || "Finalize"}
            </Button>
            {kotStartRoundSlot}
          </Box>
        )}

        {isPaused && !showStartNextSequence && (
          <Box sx={{ display: "flex", flexDirection: flexDir, gap: 1.5, width: "100%", alignItems: "stretch" }}>
            <Button
              variant="contained"
              size={compact ? "medium" : "large"}
              startIcon={<PlayArrowIcon />}
              onClick={() => onResume?.(exercise.te_id)}
              fullWidth
              sx={{
                flex: 1,
                ...btnSx,
                background: `linear-gradient(135deg, ${brandPrimary} 0%, ${brandPrimaryDark} 100%)`,
                boxShadow: `0 4px 12px rgba(var(--brand-primary-rgb), 0.35)`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${brandPrimaryDark} 0%, ${brandPrimary} 100%)`,
                },
              }}
            >
              {t("resume") || "Resume"}
            </Button>
            {kotStopRoundSlot}
            {showSkipSequence ? (
              <Button
                variant="outlined"
                color="warning"
                size={compact ? "medium" : "large"}
                startIcon={<FastForwardIcon />}
                onClick={() => onSkipSequence?.(exercise.te_id)}
                sx={{ flex: 1, ...btnSx, borderWidth: compact ? 1 : 2 }}
              >
                {t("liveTrainingSkipSequence") || "Skip sequence"}
              </Button>
            ) : null}
            <Button
              variant="contained"
              color="error"
              size={compact ? "medium" : "large"}
              startIcon={<StopIcon />}
              onClick={() => onFinalize?.()}
              sx={{ flex: 1, ...btnSx }}
            >
              {t("finalize") || "Finalize"}
            </Button>
            {kotStartRoundSlot}
          </Box>
        )}

        {isPaused && showStartNextSequence && (
          <Box sx={{ display: "flex", flexDirection: flexDir, gap: 1.5, width: "100%", alignItems: "stretch" }}>
            {kotStopRoundSlot}
            <Button
              variant="contained"
              color="error"
              size={compact ? "medium" : "large"}
              startIcon={<StopIcon />}
              onClick={() => onFinalize?.()}
              sx={{ flex: 1, ...btnSx }}
            >
              {t("finalize") || "Finalize"}
            </Button>
            {kotStartRoundSlot}
          </Box>
        )}

        {(isFinalized || isCancelled) && (
          <Chip
            label={
              isCancelled
                ? t("exerciseStateCancelled") || "Cancelled"
                : t("finalized") || "Finalized"
            }
            sx={{
              fontWeight: 700,
              alignSelf: "center",
              bgcolor: isCancelled
                ? "rgba(191, 108, 44, 0.12)"
                : "rgba(0,0,0,0.06)",
              color: isCancelled ? "#bf6c2c" : "text.secondary",
            }}
          />
        )}
      </Box>
    </Paper>
  );
}
